import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { User, Users, Flame, Star, X as XIcon } from "lucide-react";
import { assignablePeople } from "@/lib/roster";

// People row on the Work Order form: Site Contact, Account Manager,
// Project Manager, Assigned Techs (with Lead-Tech toggle). Extracted
// from WorkOrderTab.jsx (Feb 2026, iter174). All state lives in the
// parent — the component is purely presentational + setState calls.
export function WOPeopleCluster({
  form, setForm,
  isEditable, peopleEditable,
  clientSites, siteContactsOverlay,
  accountManagers, projectManagers, technicians,
  onAddContactClick,
}) {
  const currentSite = clientSites.find((s) => s.id === form.site_id);
  const baseContacts = (currentSite?.contacts) || [];
  const extras = siteContactsOverlay[form.site_id] || [];
  const contacts = [...baseContacts, ...extras];

  const assignedIds = form.assigned_tech_ids || [];
  const sortedAssigned = form.lead_tech_id && assignedIds.includes(form.lead_tech_id)
    ? [form.lead_tech_id, ...assignedIds.filter((id) => id !== form.lead_tech_id)]
    : assignedIds;
  const unassigned = technicians.filter((t) => !assignedIds.includes(t.id));

  return (
    <div className="border-t pt-3 space-y-2" data-testid="wo-people-cluster">
      {/* Site Contact */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="wo-site-contact">
        <div className="flex items-center gap-1.5 shrink-0 w-[155px]">
          <User className="w-4 h-4 text-amber-500" />
          <span className="font-semibold text-sm">Site contact</span>
        </div>
        <Select
          value={form.site_contact_id || "__none__"}
          onValueChange={(v) => {
            if (v === "__none__") {
              setForm((prev) => ({ ...prev, site_contact_id: "" }));
            } else if (v === "__add__") {
              onAddContactClick();
            } else {
              setForm((prev) => ({ ...prev, site_contact_id: v }));
            }
          }}
          disabled={!isEditable || !form.site_id}
        >
          <SelectTrigger className="h-7 w-48 text-xs" data-testid="select-site-contact">
            <SelectValue placeholder={form.site_id ? "Select contact" : "Pick a site first"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="text-slate-400 italic text-xs">None</SelectItem>
            {contacts.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}{c.is_primary ? " ★" : ""}
              </SelectItem>
            ))}
            {form.site_id && (
              <SelectItem value="__add__" className="text-blue-600">+ Add new contact</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Account Manager */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="wo-account-manager">
        <div className="flex items-center gap-1.5 shrink-0 w-[155px]">
          <User className="w-4 h-4 text-emerald-500" />
          <span className="font-semibold text-sm">Account manager</span>
        </div>
        <Select
          value={form.account_manager || "__none__"}
          onValueChange={(v) => {
            if (v === "__none__") {
              setForm((prev) => ({ ...prev, account_manager: "", account_manager_email: "" }));
            } else {
              const am = accountManagers.find((a) => a.name === v);
              setForm((prev) => ({ ...prev, account_manager: v, account_manager_email: am?.email || "" }));
            }
          }}
        >
          <SelectTrigger className="h-7 w-48 text-xs" data-testid="select-account-manager">
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="text-slate-400 italic text-xs"> </SelectItem>
            {assignablePeople(accountManagers, form.account_manager).map((am) => (
              <SelectItem key={am.id} value={am.name}>{am.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Project Manager */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="wo-project-manager">
        <div className="flex items-center gap-1.5 shrink-0 w-[155px]">
          <div className="relative w-4 h-4">
            <User className="w-4 h-4 text-indigo-500" />
            <Flame className="w-2.5 h-2.5 text-orange-500 absolute -top-0.5 -right-1 animate-pulse" />
          </div>
          <span className="font-semibold text-sm">Project manager <span className="text-rose-500" title="Required — Foundation @project_manager_no">*</span></span>
        </div>
        <Select
          value={form.project_manager_id || "none"}
          onValueChange={(v) => {
            if (v === "none") {
              setForm((prev) => ({ ...prev, project_manager_id: null, project_manager_name: "" }));
            } else {
              const pm = projectManagers.find((p) => p.id === v);
              setForm((prev) => ({ ...prev, project_manager_id: v, project_manager_name: pm?.name || "" }));
            }
          }}
        >
          <SelectTrigger className={`h-7 w-48 text-xs ${!form.project_manager_id ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid="select-project-manager">
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-slate-400 italic text-xs"> </SelectItem>
            {assignablePeople(projectManagers, form.project_manager_id, "id").map((pm) => (
              <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Assigned Techs — chip row + "+ Add" dropdown */}
      <div className="flex items-start gap-2 flex-wrap" data-testid="wo-assigned-techs-section">
        <div className="flex items-center gap-1.5 shrink-0 w-[155px] pt-0.5">
          <Users className="w-4 h-4 text-indigo-500" />
          <span className="font-semibold text-sm">Assigned techs</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-h-[28px]">
          {sortedAssigned.map((tid) => {
            const tech = technicians.find((t) => t.id === tid);
            if (!tech) return null;
            const isLead = form.lead_tech_id === tid;
            return (
              <span
                key={tid}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border cursor-pointer transition-all ${
                  isLead ? "font-bold ring-2 ring-offset-1" : "font-medium"
                }`}
                style={{
                  backgroundColor: `${tech.color || '#3b82f6'}${isLead ? '25' : '15'}`,
                  borderColor: `${tech.color || '#3b82f6'}${isLead ? '80' : '40'}`,
                  color: tech.color || '#3b82f6',
                  ringColor: isLead ? (tech.color || '#3b82f6') : undefined,
                }}
                onClick={() => setForm((prev) => ({
                  ...prev,
                  lead_tech_id: prev.lead_tech_id === tid ? null : tid,
                }))}
                title={isLead ? "Lead Tech (click to remove)" : "Click to set as Lead Tech"}
                data-testid={`assigned-tech-chip-${tid}`}
              >
                {isLead && <Star className="w-3 h-3 fill-current" />}
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tech.color || "#3b82f6" }} />
                {tech.name}
                {peopleEditable && (
                  <button
                    type="button"
                    className="ml-0.5 opacity-50 hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      const ids = assignedIds.filter((id) => id !== tid);
                      setForm({
                        ...form,
                        assigned_tech_ids: ids,
                        assigned_tech_id: ids[0] || null,
                        lead_tech_id: form.lead_tech_id === tid ? null : form.lead_tech_id,
                      });
                    }}
                    data-testid={`remove-tech-${tid}`}
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
          {peopleEditable && (
            <Select
              value="__add__"
              onValueChange={(v) => {
                if (v === "__add__") return;
                const ids = [...assignedIds];
                if (!ids.includes(v)) {
                  ids.push(v);
                  setForm({ ...form, assigned_tech_ids: ids, assigned_tech_id: ids[0] || null });
                }
              }}
            >
              <SelectTrigger
                className="h-7 w-auto min-w-[100px] text-xs border-dashed rounded-md px-2"
                data-testid="select-add-tech"
              >
                <SelectValue placeholder="+ Add" />
              </SelectTrigger>
              <SelectContent>
                {unassigned.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color || "#3b82f6" }} />
                      {t.name}
                    </div>
                  </SelectItem>
                ))}
                {unassigned.length === 0 && (
                  <div className="text-xs text-slate-400 py-2 px-3">All techs assigned</div>
                )}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
}
